import { atom } from "jotai";
import { FALLBACK_IMPLEMENTATION_IDS } from "@zapier/canvas-constants";
import { SelectableNodeTypes, formsTerminologyAtom } from "../diagramming";
import { GetNodeDataByTypeFnParams } from "../types";
import {
  agentsAppAtom,
  chatbotsAppAtom,
  interfacesAppAtom,
  tablesAppAtom,
} from "../apps";
import { uuid } from "../utils";

function getBaseNodeDataValues(selectedNodeType: SelectableNodeTypes) {
  switch (selectedNodeType) {
    case "zap":
      return {
        zapId: null,
        isExpanded: false,
      };
    default:
      return {};
  }
}

export const getNodeDataAtom = atom((get) => {
  return (params: GetNodeDataByTypeFnParams) => {
    const { type, position, width, height } = params;

    const tablesApp = get(tablesAppAtom);
    const interfacesApp = get(interfacesAppAtom);
    const chatbotsApp = get(chatbotsAppAtom);
    const agentsApp = get(agentsAppAtom);
    const interfacesTerms = get(formsTerminologyAtom);

    const id = uuid();

    /** Get the new text node right under user's mouse click area */
    const offSet = type === "text" ? -15 : -50;

    /** Don't set width or height or else measured width and height won't update unless you use NodeResizer */
    const baseNode = {
      id,
      position: {
        x: position.x + offSet,
        y: position.y + offSet,
      },
      data: {
        attributionSourceType: "MANUAL",
        label: "",
        ...getBaseNodeDataValues(type),
      },
    };

    switch (type) {
      case "group":
        return {
          ...baseNode,
          width, // need this or else group nodes won't draw correctly
          height, // need this or else group nodes won't draw correctly
          measured: {
            width,
            height,
          },
          type: "group",
          connectable: false /** Need this or else we might end up with edges that connect to this and auto layout will fail */,
        };

      case "default":
        return {
          ...baseNode,
          selected: true,
          type: "default",
        };

      case "text":
        return {
          ...baseNode,
          type: "text",
          data: {
            attributionSourceType: "MANUAL",
          },
          selected: true,
          connectable: false /** Need this or else we might end up with edges that connect to this and auto layout will fail */,
        };

      case "path":
        return {
          ...baseNode,
          type: "path",
        };

      case "zap":
        return {
          ...baseNode,
          selected: true,
          type: "zap",
        };

      case "chatbots":
        if (chatbotsApp?.current_implementation_id) {
          return {
            ...baseNode,
            selected: true,
            type: "default",
            data: {
              ...baseNode.data,
              app: "Chatbots by Zapier",
              services: [
                {
                  currentImplementation: {
                    id: chatbotsApp.current_implementation_id,
                  },
                },
              ],
            },
          };
        }
        return null;

      case "table":
        if (tablesApp?.current_implementation_id) {
          return {
            ...baseNode,
            selected: true,
            type: "default",
            data: {
              ...baseNode.data,
              app: "Tables by Zapier",
              services: [
                {
                  currentImplementation: {
                    id: tablesApp.current_implementation_id,
                  },
                },
              ],
            },
          };
        }
        return null;

      case "interface": {
        const interfacesAppImplementationId =
          interfacesApp?.current_implementation_id ??
          FALLBACK_IMPLEMENTATION_IDS.INTERFACES;

        if (interfacesAppImplementationId) {
          return {
            ...baseNode,
            selected: true,
            type: "default",
            data: {
              ...baseNode.data,
              app: interfacesTerms.byZapier,
              services: [
                {
                  currentImplementation: {
                    id: interfacesAppImplementationId,
                  },
                },
              ],
            },
          };
        }
        return null;
      }

      case "agent": {
        const agentsAppImplementationId =
          agentsApp?.current_implementation_id ??
          FALLBACK_IMPLEMENTATION_IDS.AGENTS;

        if (agentsAppImplementationId) {
          return {
            ...baseNode,
            selected: true,
            type: "default",
            data: {
              ...baseNode.data,
              app: "Agents by Zapier",
              services: [
                {
                  currentImplementation: {
                    id: agentsAppImplementationId,
                  },
                },
              ],
            },
          };
        }
        return null;
      }

      default:
        return null;
    }
  };
});

getNodeDataAtom.debugLabel = "getNodeDataAtom";
